import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useFavoriteStore } from '@/stores/favoriteStore';
import { usePromptStore } from '@/stores/promptStore';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from '@/components/prompt/SearchBar';
import { CategoryFilter } from '@/components/prompt/CategoryFilter';
import { PromptList } from '@/components/prompt/PromptList';
import { PromptDetail } from '@/components/prompt/PromptDetail';
import { PromptEditor } from '@/components/admin/PromptEditor';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Plus, Heart, Settings, LogOut } from 'lucide-react';
import type { Prompt, Category } from '@/types/prompt';

// 导入分类数据（暂时保留静态数据）
import categoriesData from '@/data/categories.json';

export default function DashboardPage() {
  const { user, logout, canCreatePrompt } = useAuthStore();
  const { loadFavorites } = useFavoriteStore();
  const { prompts, isLoading, loadPrompts } = usePromptStore();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const categories = categoriesData as Category[];

  // 加载收藏数据
  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user, loadFavorites]);

  // 当筛选条件改变时重新加载 Prompt 列表
  useEffect(() => {
    loadPrompts({
      category: selectedCategory || undefined,
      search: searchQuery || undefined,
    });
  }, [selectedCategory, searchQuery, loadPrompts]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewDetail = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setIsDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setTimeout(() => setSelectedPrompt(null), 300);
  };

  const handleCreatePrompt = () => {
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    loadPrompts({
      category: selectedCategory || undefined,
      search: searchQuery || undefined,
    });
  };

  return (
    <div className="relative min-h-screen">
      {/* 头部导航 */}
      <header className="glass border-b border-border/50 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <h1 className="text-xl md:text-2xl font-bold">Prompt Library</h1>

          {/* 桌面端导航 */}
          <div className="hidden lg:flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              欢迎, {user?.username}
            </span>
            <ThemeToggle />
            {canCreatePrompt() && (
              <Button
                variant="default"
                size="sm"
                onClick={handleCreatePrompt}
              >
                <Plus className="h-4 w-4 mr-2" />
                创建 Prompt
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
              >
                管理面板
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/favorites')}
            >
              我的收藏
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              退出登录
            </Button>
          </div>

          {/* 移动端导航 */}
          <div className="flex lg:hidden items-center gap-2">
            <ThemeToggle />
            {canCreatePrompt() && (
              <Button
                variant="default"
                size="sm"
                onClick={handleCreatePrompt}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">创建</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/favorites')}
            >
              <Heart className="h-4 w-4" />
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto p-4 md:p-8">
        {/* 搜索和筛选 */}
        <div className="mb-6 md:mb-8 space-y-3 md:space-y-4">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="搜索 Prompt 标题、描述或标签..."
          />
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        </div>

        {/* 统计信息 */}
        <div className="mb-4 md:mb-6 flex items-center justify-between">
          <div className="text-xs md:text-sm text-muted-foreground">
            找到 <span className="font-semibold">{prompts.length}</span> 个 Prompt
          </div>
          {(searchQuery || selectedCategory) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory(null);
              }}
            >
              清除筛选
            </Button>
          )}
        </div>

        {/* Prompt 列表 */}
        <PromptList
          prompts={prompts}
          onViewDetail={handleViewDetail}
          isLoading={isLoading}
        />
      </main>

      {/* Prompt 详情模态框 */}
      <PromptDetail
        prompt={selectedPrompt}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
      />

      {/* Prompt 编辑器对话框 */}
      <PromptEditor
        prompt={null}
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
      />
    </div>
  );
}

